import { type ReactNode } from 'react';

import { cn } from '@apps/web/lib/utils';

/*
 * Admin page shells — the structural primitives of the admin design
 * system (see admin_theme.css for the token layer). Every admin page
 * composes AdminPage > AdminPageHeader + AdminPageBody; sections and
 * cards provide the vertical rhythm inside the body.
 *
 * Round 3 aesthetic: paper canvas, ink on paper, hairline everywhere,
 * no shadow at rest, no root padding. Header sticks with a translucent
 * canvas so tables and lists slide beneath it cleanly.
 */

export function AdminPage({
    className,
    children,
    ...props
}: React.ComponentProps<'div'>) {
    return (
        <div
            data-slot="admin-page"
            className={cn(
                // Round 4: the page owns full viewport height, not
                // just the parent's height — sticky headers and body
                // scrolling need a real min-h-screen anchor so nested
                // routes never squish the canvas.
                'admin-theme bg-admin-canvas text-admin-ink text-admin-base flex h-full min-h-screen flex-col antialiased',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}

export function AdminPageHeader({
    title,
    meta,
    actions,
    className,
    children,
    ...props
}: React.ComponentProps<'header'> & {
    readonly title: ReactNode;
    readonly meta?: ReactNode;
    readonly actions?: ReactNode;
}) {
    return (
        <header
            data-slot="admin-page-header"
            className={cn(
                'border-admin-border bg-admin-canvas/85 sticky top-0 z-10 shrink-0 border-b px-6 backdrop-blur-md',
                className,
            )}
            {...props}
        >
            {/* Editorial title row — 32px display heading (weight 700,
                -0.02em tracking) breathing on the 56px chrome the
                contract calls for. 12px top + 32px title (line-height
                1) + 12px bottom = 56 exact. Actions + meta baseline-
                align to the title. */}
            <div
                data-testid="admin-page-header-title-row"
                className="flex min-h-[56px] flex-wrap items-baseline gap-3 py-3"
            >
                <h1 className="text-admin-ink text-[32px] leading-[1] font-bold tracking-[-0.02em] text-balance">
                    {title}
                </h1>
                {meta !== undefined && (
                    <div className="text-admin-sm text-admin-ink-muted admin-tabular tabular-nums">
                        {meta}
                    </div>
                )}
                {actions !== undefined && (
                    <div className="ml-auto flex items-center gap-2">
                        {actions}
                    </div>
                )}
            </div>
            {children !== undefined && children !== null && (
                <div
                    data-testid="admin-page-header-toolbar"
                    className="flex flex-col gap-2.5 pb-3"
                >
                    {children}
                </div>
            )}
        </header>
    );
}

export function AdminPageBody({
    padded = true,
    className,
    children,
    ...props
}: React.ComponentProps<'div'> & {
    /**
     * Whether the body renders its default 24px padding. Round 4
     * flips the default to `true` so admin pages inherit the
     * editorial rhythm without opting in. Full-bleed cases (embedded
     * tables) can pass `padded={false}` to opt out.
     */
    readonly padded?: boolean;
}) {
    return (
        <div
            data-slot="admin-page-body"
            className={cn(
                'min-h-0 flex-1 overflow-y-auto',
                padded && 'p-6',
                className,
            )}
            {...props}
        >
            {children}
        </div>
    );
}

export function AdminSection({
    title,
    description,
    actions,
    className,
    children,
    ...props
}: React.ComponentProps<'section'> & {
    readonly title?: ReactNode;
    readonly description?: ReactNode;
    readonly actions?: ReactNode;
}) {
    return (
        <section
            data-slot="admin-section"
            // Round 4: sections carry a 32px vertical padding so the
            // page body reads as a rhythmed sequence of blocks rather
            // than a flush wall. Pages that need a tighter stack can
            // override with a py-0 utility.
            className={cn('flex flex-col gap-3 py-8', className)}
            {...props}
        >
            {(title !== undefined || actions !== undefined) && (
                <div className="flex items-baseline gap-3">
                    <div className="flex flex-col gap-0.5">
                        {title !== undefined && (
                            <h2 className="text-admin-lg text-admin-ink font-semibold tracking-[-0.005em] text-balance">
                                {title}
                            </h2>
                        )}
                        {description !== undefined && (
                            <p className="text-admin-sm text-admin-ink-muted text-pretty">
                                {description}
                            </p>
                        )}
                    </div>
                    {actions !== undefined && (
                        <div className="ml-auto flex items-center gap-2">
                            {actions}
                        </div>
                    )}
                </div>
            )}
            {children}
        </section>
    );
}

export function AdminCard({
    title,
    description,
    actions,
    flush = false,
    className,
    children,
    ...props
}: React.ComponentProps<'div'> & {
    readonly title?: ReactNode;
    readonly description?: ReactNode;
    readonly actions?: ReactNode;
    /** Remove content padding (full-bleed tables inside cards). */
    readonly flush?: boolean;
}) {
    const hasHeader = title !== undefined || actions !== undefined;
    return (
        <div
            data-slot="admin-card"
            className={cn(
                // Hover shifts the hairline toward Phoebe purple so
                // cards read as picked-up without lifting a shadow —
                // the border shift itself is the elevation cue and it
                // now inherits the brand hue on interaction.
                'rounded-admin-md border-admin-border bg-admin-surface hover:border-admin-purple-border border transition-[border-color] duration-[var(--admin-dur-fast)] ease-[var(--admin-ease-in-out)]',
                // Full-bleed children (tables) must clip to the card radius.
                flush && 'overflow-hidden',
                className,
            )}
            {...props}
        >
            {hasHeader && (
                <div
                    data-slot="admin-card-header"
                    className={cn(
                        // 40px content height, 12px hpadding — hairline
                        // divider only when the card is not flush.
                        'flex h-10 items-center gap-3 px-3',
                        !flush && 'border-admin-border border-b',
                    )}
                >
                    <div className="flex min-w-0 flex-col gap-0.5">
                        {title !== undefined && (
                            // Round 4: card titles read as micro-caps
                            // section labels rather than sentence-case
                            // headings — 11px uppercase, +6% tracking,
                            // ink-tertiary. Distinguishes a card from a
                            // section heading (which stays 14px ink).
                            <h3 className="text-admin-xs text-admin-ink-muted font-medium tracking-[0.06em] uppercase">
                                {title}
                            </h3>
                        )}
                        {description !== undefined && (
                            <p className="text-admin-sm text-admin-ink-muted text-pretty">
                                {description}
                            </p>
                        )}
                    </div>
                    {actions !== undefined && (
                        <div className="ml-auto flex shrink-0 items-center gap-2">
                            {actions}
                        </div>
                    )}
                </div>
            )}
            {children !== undefined && children !== null && (
                <div
                    data-slot="admin-card-content"
                    className={cn(!flush && 'px-3 py-3')}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

export function AdminStat({
    label,
    value,
    hint,
    className,
    ...props
}: React.ComponentProps<'div'> & {
    readonly label: ReactNode;
    readonly value: ReactNode;
    readonly hint?: ReactNode;
}) {
    return (
        <div
            data-slot="admin-stat"
            className={cn(
                'rounded-admin-md border-admin-border bg-admin-surface border px-4 py-3',
                className,
            )}
            {...props}
        >
            {/* Micro-caps label — 11px, uppercase, +6% tracking. */}
            <div className="text-admin-xs text-admin-ink-muted font-medium tracking-[0.06em] uppercase">
                {label}
            </div>
            <div className="text-admin-2xl text-admin-ink admin-tabular mt-1 font-semibold tabular-nums">
                {value}
            </div>
            {hint !== undefined && (
                <div className="text-admin-sm text-admin-ink-muted admin-tabular mt-0.5 tabular-nums">
                    {hint}
                </div>
            )}
        </div>
    );
}
